import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { SignupLayout } from '../../../layouts';
import CustomInputField from '../../FundingCalculator/components/CustomInputField';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CustomSelect from '../../FundingCalculator/components/CustomSelectField';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { InfiniteSearchSelect } from '@components';
import useGetCountriesList from '@hooks/useGetCountriesList';

const SignupPage: FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t: translation } = useTranslation();
  const { countries, onSearchChange } = useGetCountriesList();
  const ATTRIBUTED_CHANNEL_OPTIONS = [
    {
      value: 'PvX Team',
      label: translation('signup.attributed_channel_options.pvx_team'),
    },
    {
      value: 'Conference',
      label: translation('signup.attributed_channel_options.conference'),
    },
    {
      value: 'Investor',
      label: translation('signup.attributed_channel_options.investor'),
    },
    {
      value: 'Founder',
      label: translation('signup.attributed_channel_options.founder'),
    },
    {
      value: 'Advertisement',
      label: translation('signup.attributed_channel_options.advertisement'),
    },
    {
      value: 'Other',
      label: translation('signup.attributed_channel_options.other'),
    },
  ];

  const MONTHLY_MARKETING_SPENDS_OPTIONS = [
    {
      value: 'Under $100,000',
      label: translation('signup.monthly_marketing_spends_options.under_100k'),
    },
    {
      value: '$100,000 - $250,000',
      label: translation('signup.monthly_marketing_spends_options.100k_250k'),
    },
    {
      value: '$250,000 - $500,000',
      label: translation('signup.monthly_marketing_spends_options.250k_500k'),
    },
    {
      value: '$500,000 - $1,000,000',
      label: translation('signup.monthly_marketing_spends_options.500k_1m'),
    },
    {
      value: '> $1,000,000',
      label: translation('signup.monthly_marketing_spends_options.over_1m'),
    },
  ];
  // Form State
  const [name, setName] = useState<string>('Puzzler Pro');
  const [companyName, setCompanyName] = useState<string>('PvX Puzzler Pro LLC');
  const [companyHQ, setCompanyHQ] = useState<string>('US');
  const [email, setEmail] = useState<string>('demo@pvxpartners.com');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [playstoreLink, setPlaystoreLink] = useState<string>(
    'https://play.google.com/store/apps/details?id=com.pvx.puzzlerpro&hl=en_IN',
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [appstoreLink, setAppstoreLink] = useState<string>(
    'https://apps.apple.com/in/app/puzzlerpro/id99309054',
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [websiteLink, setWebsiteLink] = useState<string>('https://www.puzzlerpro.com');
  const [avgMonthlyMarketingSpend, setAvgMonthlyMarketingSpend] = useState<string>(
    MONTHLY_MARKETING_SPENDS_OPTIONS[2].value,
  );
  const [acceptDataAccessTerms, setAcceptDataAccessTerms] = useState(false);
  const [acceptTermsAndConditions, setAcceptTermsAndConditions] = useState(false);
  const [attributedChannel, setAttributedChannel] = useState<string>(
    ATTRIBUTED_CHANNEL_OPTIONS[0].value,
  );
  const [acceptMarketingCommunications, setAcceptMarketingCommunications] = useState(false);
  const [touched, setTouched] = useState({
    name: false,
    companyName: false,
    companyHQ: false,
    email: false,

    avgMonthlyMarketingSpend: false,
  });

  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [isUserCreated, setIsUserCreated] = useState(false);

  useEffect(() => {
    if (isCreatingUser) {
      setTimeout(() => {
        setIsCreatingUser(false);
        setIsUserCreated(true);
      }, 4000);
    }
  }, [isCreatingUser]);

  const handleAttributedChannelChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setAttributedChannel(event.target.value);
  };

  const registerUser = async () => {
    setIsCreatingUser(true);
  };

  return (
    <SignupLayout>
      <Box
        sx={{
          paddingTop: '2rem',
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(1, 1fr)' : 'repeat(2, 1fr)',
          gap: '1rem',
        }}
      >
        <CustomInputField
          label={translation('signup.full_name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => setTouched({ ...touched, name: true })}
          error={false}
        />
        <CustomInputField
          label={translation('signup.work_email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => setTouched({ ...touched, email: true })}
          error={false}
        />

        <CustomInputField
          label={translation('signup.company_name')}
          value={companyName}
          disabled={true}
          onChange={(e) => setCompanyName(e.target.value)}
          onBlur={() => setTouched({ ...touched, companyName: true })}
          error={false}
        />
        <Box
          sx={{
            border: '1px solid #989CA24D',
            borderRadius: '20px',
            backgroundColor: 'white',
            paddingTop: '22px',
          }}
        >
          <Typography
            sx={{
              fontSize: '11px',
              lineHeight: '12px',
              fontWeight: 700,
              color: '#162936',
              paddingLeft: '22px',
            }}
          >
            {translation('signup.company_hq')}
          </Typography>
          <InfiniteSearchSelect
            placeholder={''}
            options={countries}
            hasNextPage={false}
            isFetching={false}
            fetchNextPage={() => {}}
            onSelect={(option) => {
              setCompanyHQ(option.value);
              setTouched({ ...touched, companyHQ: true });
            }}
            isLoading={false}
            isError={false}
            errorText="Error loading countries"
            noOptionsText="No countries found"
            searchEnabled={true}
            onSearchChange={onSearchChange}
            selectedValue={
              countries.find((country) => country.value === companyHQ) || { label: '', value: '' }
            }
            selectStyles={{
              border: 'none',
              paddingLeft: '10px',
            }}
            menuStyles={{
              paddingTop: '8px',
              top: 60,
              borderRadius: '4px',
              border: 'none',
              boxShadow: '0px 4px 12px 0px #00000040',
              maxHeight: '300px',
            }}
            menuItemStyles={{
              fontSize: '15px',
              color: '#162936',
              fontWeight: 550,
              lineHeight: '18px',
              letterSpacing: '-0.6px',
            }}
          />
        </Box>
        <CustomSelect
          label={translation('signup.monthly_marketing_spends')}
          value={avgMonthlyMarketingSpend}
          onChange={(e) => setAvgMonthlyMarketingSpend(e.target.value)}
          onBlur={() => setTouched({ ...touched, avgMonthlyMarketingSpend: true })}
          options={MONTHLY_MARKETING_SPENDS_OPTIONS}
          error={false}
        />
        <CustomSelect
          label={translation('signup.how_did_you_hear')}
          value={attributedChannel}
          onChange={handleAttributedChannelChange}
          options={ATTRIBUTED_CHANNEL_OPTIONS}
          error={false}
        />
      </Box>

      <Box
        sx={{
          marginTop: '2rem',
          backgroundColor: '#162936',
          padding: '1.5rem',
          borderRadius: '2rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
        }}
      >
        {isUserCreated ? (
          <>
            <Stack direction={'row'} alignItems={'center'} gap={'1.25rem'}>
              <Box
                borderRadius={'50%'}
                minHeight={66}
                minWidth={66}
                sx={{
                  backgroundColor: '#2E4A4F',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isUserCreated ? (
                  <CheckIcon sx={{ color: '#7FBCAD' }} />
                ) : (
                  <ErrorOutlineIcon sx={{ color: '#7FBCAD' }} />
                )}
              </Box>

              <Stack>
                <Typography
                  fontSize={isMobile ? '1.25rem' : '1.375rem'}
                  lineHeight={'1.875rem'}
                  fontWeight={700}
                  color={'#F6F8F9'}
                >
                  {isUserCreated
                    ? translation('signup.account_created')
                    : translation('signup.already_registered', { name })}
                </Typography>
                <Typography
                  fontSize={isMobile ? '0.75rem' : '1rem'}
                  lineHeight={'1.375rem'}
                  fontWeight={700}
                  color={'#B4B4B4'}
                >
                  {isUserCreated
                    ? translation('signup.please_sign_in')
                    : translation('signup.sign_in_using', {
                        maskedEmail: `${email[0]}${Array(email.split('@')[0].length - 1)
                          .fill('*')
                          .join('')}@${email.split('@')[1]}`,
                      })}
                </Typography>
              </Stack>
            </Stack>

            <Button
              variant="contained"
              disableElevation
              disableFocusRipple
              disableRipple
              disableTouchRipple
              onClick={() => {
                navigate('/auth');
              }}
              sx={{
                width: isMobile ? '100%' : '315px',
                maxWidth: '315px',
                paddingTop: '1rem',
                paddingBottom: '1rem',
                borderRadius: '1.5rem',
                fontSize: '1.375rem',
                fontWeight: 700,
                lineHeight: '1.875rem',
                textTransform: 'none',
              }}
            >
              {translation('signup.sign_in')}
            </Button>
          </>
        ) : (
          <>
            <Typography
              fontSize={'1rem'}
              fontWeight={700}
              lineHeight={'1.375rem'}
              color={'#F6F8F9'}
            >
              {translation('signup.sign_up_access')}
            </Typography>
            <Button
              variant="contained"
              onClick={registerUser}
              disabled={isCreatingUser || !acceptTermsAndConditions || !acceptDataAccessTerms}
              disableElevation
              disableFocusRipple
              disableRipple
              disableTouchRipple
              sx={{
                width: isMobile ? '100%' : '315px',
                maxWidth: '315px',
                paddingTop: '1rem',
                paddingBottom: '1rem',
                borderRadius: '1.5rem',
                fontSize: '1.375rem',
                fontWeight: 700,
                lineHeight: '1.875rem',
                textTransform: 'none',
                ':disabled': {
                  backgroundColor: '#2E4A4F',
                },
              }}
            >
              {isCreatingUser ? (
                <>
                  <CircularProgress
                    size={'1.375rem'}
                    sx={{
                      marginRight: '1rem',
                    }}
                  />
                  <Typography
                    fontSize={'1.375rem'}
                    lineHeight={'1.875rem'}
                    fontWeight={700}
                    color={'#F6F8F9'}
                  >
                    {translation('signup.creating_account')}
                  </Typography>
                </>
              ) : (
                <>{translation('signup.sign_up')}</>
              )}
            </Button>{' '}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                width: '90%',
                fontSize: '12px',
                color: false ? '#F6F8F9' : '#E4E3DBC2',
                lineHeight: '16px',
                fontWeight: 500,
              }}
            >
              <Checkbox
                value={acceptMarketingCommunications}
                onChange={(e: any) => setAcceptMarketingCommunications(e.target.checked)}
                sx={{
                  padding: '0px',
                  color: '#2F736E',
                  '&.Mui-checked': {
                    color: '#2F736E',
                  },
                }}
              />
              <Typography
                fontSize={'0.75rem'}
                fontWeight={500}
                lineHeight={'1rem'}
                color={'#E4E3DBC2'}
              >
                {translation('signup.marketing_communications_opt_in')}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'start',
                gap: '10px',
                width: '90%',
                fontSize: '12px',
                color: false ? '#F6F8F9' : '#F6F8F999',
                lineHeight: '16px',
                fontWeight: 500,
              }}
            >
              <Checkbox
                value={acceptDataAccessTerms}
                onChange={(e: any) => setAcceptDataAccessTerms(e.target.checked)}
                sx={{
                  padding: '0px',
                  color: '#2F736E',
                  '&.Mui-checked': {
                    color: '#2F736E',
                  },
                }}
              />
              <Typography
                fontSize={'0.75rem'}
                fontWeight={500}
                lineHeight={'1rem'}
                color={'#E4E3DBC2'}
              >
                {translation('signup.data_access_terms')}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                width: '90%',
                fontSize: '12px',
                color: false ? '#F6F8F9' : '#E4E3DBC2',
                lineHeight: '16px',
                fontWeight: 500,
              }}
            >
              <Checkbox
                value={acceptTermsAndConditions}
                onChange={(e: any) => setAcceptTermsAndConditions(e.target.checked)}
                sx={{
                  padding: '0px',
                  color: '#2F736E',
                  '&.Mui-checked': {
                    color: '#2F736E',
                  },
                }}
              />

              <Typography
                fontSize={'0.75rem'}
                fontWeight={500}
                lineHeight={'1rem'}
                color={'#E4E3DBC2'}
              >
                <Trans
                  i18nKey={'signup.terms_and_privacy'}
                  components={{
                    1: (
                      <a
                        href="https://pvxpartners.com/terms-of-use"
                        aria-label="Terms of Use"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#E4E3DBC2',
                          textDecoration: 'underline',
                        }}
                      />
                    ),
                    2: (
                      <a
                        href="https://pvxpartners.com/privacy-policy"
                        aria-label="Privacy Policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#E4E3DBC2',
                          textDecoration: 'underline',
                        }}
                      />
                    ),
                  }}
                />
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </SignupLayout>
  );
};

export default SignupPage;
